import { Row } from "@tanstack/react-table";
import { observer } from "mobx-react-lite";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { Chart, type ChartRange } from "@/components/Chart";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { Skeleton } from "@/components/ui/skeleton";
import {
	AlertCircle,
	BarChart2,
	Check,
	Clock,
	Info,
	Minus,
	RotateCcw,
	TrendingDown,
	TrendingUp,
	X,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PARAMETER_IDS } from "@/config/parameters";
import { pValueToConfidenceLabel } from "./blockageUtils";
import { structure } from "@/endpoints/dryp/structure";
import { event } from "@/endpoints/dryp/event";
import { blockageFeedback } from "@/endpoints/dryp/blockageFeedback";
import { useRainData } from "@/hooks/useRainData";
import { type RainOverlay } from "@/components/Chart";
import { SourceInfoBadge } from "@/pages/Dashboard/Structures/Structure/components/SourceInfo";
import { type DataSourceInfo } from "@/endpoints/rain/rain";
import { type StructureTimePeriodsValue } from "@/config/date";

const BLOCKAGE_RAIN_AGG = { rollingWindowMinutes: 60, unit: "mm/h" } as const;

interface BlockageChartProps {
	structureId: Dryp.StructureId;
	structureName: string;
	structureLat: number;
	structureLon: number;
	allParameters: Dryp.StructureParameter[] | null;
	from: string;
	to: string;
	ranges: ChartRange[];
	rainOverlay: RainOverlay | null;
	sourceInfo: DataSourceInfo | null;
	showSourceBadge?: boolean;
}

const BlockageChart = memo<BlockageChartProps>(
	({
		structureId,
		structureName,
		structureLat,
		structureLon,
		allParameters,
		from,
		to,
		ranges,
		rainOverlay,
		sourceInfo,
		showSourceBadge = true,
	}) => {
		const chartParameters = BLOCKAGE_PARAMETER_IDS.map(id =>
			(allParameters || []).find(p => p.id === (id as unknown as Dryp.ParameterId)),
		).filter(Boolean) as Dryp.StructureParameter[];

		if (chartParameters.length === 0) {
			return (
				<div className="flex flex-col items-center justify-center py-8 border rounded-lg gap-2">
					<AlertCircle className="h-5 w-5 text-muted-foreground" />
					<p className="text-muted-foreground text-sm">No chart parameters configured for this structure</p>
				</div>
			);
		}

		const legendExtra =
			showSourceBadge && sourceInfo ? (
				<SourceInfoBadge
					sourceInfo={sourceInfo}
					structureLat={structureLat}
					structureLon={structureLon}
					structureName={structureName}
					compact
				/>
			) : undefined;

		return (
			<Chart
				structureId={structureId}
				parameters={chartParameters}
				from={from}
				to={to}
				showControls={true}
				height={400}
				ranges={ranges}
				rainOverlay={rainOverlay ?? undefined}
				legendExtra={legendExtra}
			/>
		);
	},
);

interface ExpandedBlockagePanelProps {
	row: Row<Dryp.Blockage>;
}

interface BlockageDetailsPanelProps {
	blockage: Dryp.Blockage;
	from?: string;
	to?: string;
	timePeriod?: StructureTimePeriodsValue;
	showSourceBadge?: boolean;
}

const BLOCKAGE_PARAMETER_IDS = [PARAMETER_IDS.WaterLevel];
// Backend blockage stats are computed by the Python SPC worker on a fixed
// 30-day rolling window (see blockages/src/blockages/worker.py OVERLAP_DAYS),
// independent of the chart's time-period picker.
const BLOCKAGE_STATS_PERIOD_LABEL = "Last 30 days";
const RAIN_EVENT_TYPE_ID = 2;

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
	<Tooltip>
		<TooltipTrigger asChild>
			<Info className="h-3 w-3 text-muted-foreground/50 hover:text-muted-foreground cursor-default shrink-0" />
		</TooltipTrigger>
		<TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
	</Tooltip>
);

const TrendDirectionBadge: React.FC<{ direction: Dryp.BlockageTrendDirection | null | undefined }> = ({
	direction,
}) => {
	const configMap: Record<string, { Icon: React.ComponentType<{ className?: string }>; className: string }> = {
		Rising: { Icon: TrendingUp, className: "text-red-600 bg-red-50 border-red-200" },
		Falling: { Icon: TrendingDown, className: "text-green-600 bg-green-50 border-green-200" },
		Stable: { Icon: Minus, className: "text-gray-600 bg-gray-50 border-gray-200" },
	};
	const config = direction ? configMap[direction] : undefined;

	if (!config) return <span className="text-sm text-muted-foreground">—</span>;

	return (
		<span
			className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${config.className}`}
		>
			<config.Icon className="h-3.5 w-3.5" />
			{direction}
		</span>
	);
};

export const BlockageDetailsPanel: React.FC<BlockageDetailsPanelProps> = observer(
	({ blockage, from: fromProp, to: toProp, timePeriod: timePeriodProp, showSourceBadge = true }) => {
		const structureId = blockage.structureId;
		const parametersApi = useAsyncStateStore(structure.getStructureParameters);
		const rainEventsApi = useAsyncStateStore(event.getEvents);

		const [feedbackMap, setFeedbackMap] = useState<Map<number, Dryp.BlockageEventFeedback>>(
			() => new Map((blockage.eventFeedback ?? []).map(f => [f.eventStartMs, f])),
		);
		const handleFeedbackSubmitted = useCallback(
			(startMs: number, endMs: number, classification: Dryp.BlockageFeedbackClassification) => {
				setFeedbackMap(prev => {
					const next = new Map(prev);
					next.set(startMs, {
						eventStartMs: startMs,
						eventEndMs: endMs,
						classification,
						classifiedBy: "you",
						classifiedAt: new Date().toISOString(),
					});
					return next;
				});
			},
			[],
		);

		const handleFeedbackRemoved = useCallback((startMs: number) => {
			setFeedbackMap(prev => {
				const next = new Map(prev);
				next.delete(startMs);
				return next;
			});
		}, []);

		const [selectedEvent, setSelectedEvent] = useState<{ x: number; x2: number } | null>(null);
		const [dialogSubmitting, setDialogSubmitting] = useState(false);
		const [dialogError, setDialogError] = useState<string | null>(null);

		const handleDialogClassify = useCallback(
			async (classification: Dryp.BlockageFeedbackClassification) => {
				if (!selectedEvent) return;
				setDialogSubmitting(true);
				setDialogError(null);
				try {
					await blockageFeedback.submit(blockage.id, selectedEvent.x, selectedEvent.x2, classification);
					handleFeedbackSubmitted(selectedEvent.x, selectedEvent.x2, classification);
					setSelectedEvent(null);
				} catch {
					setDialogError("Failed to submit — please try again.");
				} finally {
					setDialogSubmitting(false);
				}
			},
			[selectedEvent, blockage.id, handleFeedbackSubmitted],
		);

		const handleDialogUndo = useCallback(
			async (startMs: number) => {
				setDialogSubmitting(true);
				setDialogError(null);
				try {
					await blockageFeedback.remove(blockage.id, startMs);
					handleFeedbackRemoved(startMs);
				} catch {
					setDialogError("Failed to undo — please try again.");
				} finally {
					setDialogSubmitting(false);
				}
			},
			[blockage.id, handleFeedbackRemoved],
		);

		const hasCallerRange = Boolean(fromProp && toProp);
		const defaultRange = useMemo(() => {
			const now = DateTime.utc();
			return { from: now.minus({ days: 30 }).toISO()!, to: now.toISO()! };
		}, []);
		const from = hasCallerRange ? fromProp! : defaultRange.from;
		const to = hasCallerRange ? toProp! : defaultRange.to;
		const timePeriod: StructureTimePeriodsValue = timePeriodProp ?? "last30d";
		const { rainOverlay, sourceInfo } = useRainData(
			blockage.coordinate.latitude,
			blockage.coordinate.longitude,
			from,
			to,
			timePeriod,
			BLOCKAGE_RAIN_AGG,
		);
		useEffect(() => {
			parametersApi.fetch(structureId);
		}, [structureId]);

		useEffect(() => {
			rainEventsApi.fetch(
				{ pageNumber: 0, pageSize: 10000 },
				undefined,
				{ structureIds: [structureId] },
				[RAIN_EVENT_TYPE_ID],
				from,
				to,
			);
		}, [structureId, from, to]);

		const { loading, loaded, data: allParameters } = parametersApi;
		const rainEvents = rainEventsApi.data?.items ?? [];

		const handleBlockageRangeClick = useCallback((item: { x: number; x2: number }) => setSelectedEvent(item), []);

		const ranges: ChartRange[] = useMemo(() => {
			const allEvents = blockage.events ?? [];
			const classifiedMap = new Map([...feedbackMap.entries()].map(([k, v]) => [k, v.classification]));
			return [
				{
					key: "rain",
					name: "Rain Event",
					color: "#3b82f6",
					data: rainEvents.map(e => ({ x: e.start, x2: e.end })),
				},
				{
					key: "blockage-unreviewed",
					name: "Blockage Event",
					color: "#f97316",
					data: allEvents
						.filter(([startMs]) => !classifiedMap.has(startMs))
						.map(([startMs, , endMs]) => ({ x: startMs, x2: endMs })),
					onRangeClick: handleBlockageRangeClick,
				},
				{
					key: "blockage-confirmed",
					name: "Confirmed Blockage",
					color: "#f97316",
					opacity: 0.3,
					hiddenFromLegend: true,
					data: allEvents
						.filter(([startMs]) => classifiedMap.get(startMs) === "TruePositive")
						.map(([startMs, , endMs]) => ({ x: startMs, x2: endMs })),
					onRangeClick: handleBlockageRangeClick,
				},
				{
					key: "blockage-false-positive",
					name: "False Positive",
					color: "#f97316",
					opacity: 0.07,
					hiddenFromLegend: true,
					data: allEvents
						.filter(([startMs]) => classifiedMap.get(startMs) === "FalsePositive")
						.map(([startMs, , endMs]) => ({ x: startMs, x2: endMs })),
					onRangeClick: handleBlockageRangeClick,
				},
			];
		}, [rainEvents, blockage.events, feedbackMap, handleBlockageRangeClick]);

		const { minValue, maxValue, avgValue, dataQuality, accumulatedRain, trend } = blockage;
		const eventsCount = (blockage.events ?? []).length;

		const monthsToLimitColor =
			trend?.buildupMonthsToLimit == null
				? ""
				: trend.buildupMonthsToLimit > 12
					? "text-green-600"
					: trend.buildupMonthsToLimit > 6
						? "text-yellow-600"
						: "text-red-600";

		return (
			<div className="px-3 py-4 space-y-4">
				{/* Stats + Trend panel */}
				<div className="rounded-lg border overflow-hidden bg-muted/30">
					{/* Stats bar */}
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
						{/* Min / Avg / Max combined */}
						<div className="flex min-w-0 flex-col gap-0.5 px-3 py-3 border-b sm:border-r lg:border-b-0 border-border/60">
							<span className="flex items-center gap-1 text-xs text-muted-foreground">
								Water Level{" "}
								<InfoTooltip text="Min, average, and max water level recorded over the last 30 days. This is computed by the backend on a fixed 30-day rolling window and does not follow the chart's time-period picker." />
							</span>
							<div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-semibold">
								<span title="Min">{minValue !== null ? `${minValue.toFixed(2)} m` : "—"}</span>
								<span className="text-xs font-normal text-muted-foreground">·</span>
								<span title="Avg">{avgValue !== null ? `${avgValue.toFixed(2)} m` : "—"}</span>
								<span className="text-xs font-normal text-muted-foreground">·</span>
								<span title="Max">{maxValue !== null ? `${maxValue.toFixed(2)} m` : "—"}</span>
							</div>
							<span className="text-[10px] text-muted-foreground">
								min · avg · max · {BLOCKAGE_STATS_PERIOD_LABEL.toLowerCase()}
							</span>
						</div>
						{/* Events with period */}
						<div className="flex min-w-0 flex-col gap-0.5 px-3 py-3 border-b lg:border-r lg:border-b-0 border-border/60">
							<span className="flex items-center gap-1 text-xs text-muted-foreground">
								Dry-weather blockages{" "}
								<InfoTooltip text="Number of blockage events detected during dry-weather conditions over the last 30 days. Computed by the backend on a fixed 30-day rolling window and does not follow the chart's time-period picker." />
							</span>
							{eventsCount > 0 ? (
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="text-sm font-semibold cursor-help underline decoration-dotted underline-offset-2 decoration-muted-foreground/40 self-start">
											{eventsCount}
										</span>
									</TooltipTrigger>
									<TooltipContent className="max-w-xs p-2 text-xs space-y-1">
										<div className="font-medium">Blockage events</div>
										<ul className="space-y-0.5 text-muted-foreground">
											{(blockage.events ?? []).map(([startMs, , endMs], i) => {
												const start = DateTime.fromMillis(startMs, { zone: "utc" });
												const end = DateTime.fromMillis(endMs, { zone: "utc" });
												const totalMin = Math.max(0, Math.round((endMs - startMs) / 60000));
												const h = Math.floor(totalMin / 60);
												const m = totalMin % 60;
												const duration = h > 0 ? `${h}h ${m}m` : `${m}m`;
												return (
													<li key={`${startMs}-${i}`}>
														{start.toFormat("dd LLL HH:mm")} →{" "}
														{end.toFormat(
															start.hasSame(end, "day") ? "HH:mm" : "dd LLL HH:mm",
														)}
														<span className="ml-1">({duration})</span>
													</li>
												);
											})}
										</ul>
									</TooltipContent>
								</Tooltip>
							) : (
								<span className="text-sm font-semibold">{eventsCount}</span>
							)}
							<span className="text-[10px] text-muted-foreground">{BLOCKAGE_STATS_PERIOD_LABEL}</span>
						</div>
						<div className="flex min-w-0 flex-col gap-0.5 px-3 py-3 border-b sm:border-r sm:border-b-0 lg:border-r border-border/60">
							<span className="flex items-center gap-1 text-xs text-muted-foreground">
								Accumulated Rain
								<InfoTooltip text="Total rainfall accumulated over the last 24 hours at this location." />
							</span>
							<span className="text-sm font-semibold">
								{accumulatedRain != null ? `${accumulatedRain} mm` : "—"}
							</span>
							<span className="text-[10px] text-muted-foreground">last 24 hours</span>
						</div>
						<div className="flex min-w-0 flex-col gap-0.5 px-3 py-3">
							<span className="flex items-center gap-1 text-xs text-muted-foreground">
								Data Coverage
								<InfoTooltip text="Percentage of expected sensor readings that were actually received. E.g. a sensor reporting every 15 min over 30 days expects 2,880 readings — if 2,750 arrived, coverage is 95.5%. Below 70% trends may be less reliable; below 50% results should be treated with caution." />
							</span>
							<span className="text-sm font-semibold">
								{dataQuality != null ? `${dataQuality.toFixed(1)}%` : "—"}
							</span>
							{dataQuality != null && (
								<div className="h-1 w-full rounded bg-muted mt-0.5">
									<div
										className={`h-1 rounded ${dataQuality >= 80 ? "bg-green-500" : dataQuality >= 50 ? "bg-yellow-400" : "bg-red-500"}`}
										style={{ width: `${Math.min(100, dataQuality)}%` }}
									/>
								</div>
							)}
						</div>
					</div>

					{/* Trend section */}
					{trend && (
						<div className="border-t p-4 space-y-3">
							<div className="flex items-center gap-2 text-sm font-medium">
								<BarChart2 className="h-4 w-4 text-muted-foreground" />
								Trends
							</div>
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
								{/* Event trend */}
								<div className="space-y-1">
									<div className="flex items-center gap-1 text-xs text-muted-foreground">
										Blockage Frequency Trend{" "}
										<InfoTooltip text="Whether discrete dry-weather blockage events are happening more or less often than before. Based on the trend in intervals between recent events — accelerating means the gaps are shrinking, improving means they are growing. Needs at least 3 events to assess." />
									</div>
									<TrendDirectionBadge direction={trend.eventTrend} />
									{trend.eventTrendPValue != null && (
										<div className="text-xs text-muted-foreground">
											{pValueToConfidenceLabel(trend.eventTrendPValue)} (p=
											{trend.eventTrendPValue.toFixed(2)})
										</div>
									)}
								</div>

								{/* Buildup trend */}
								<div className="space-y-1">
									<div className="flex items-center gap-1 text-xs text-muted-foreground">
										Baseline Trend{" "}
										<InfoTooltip text="Direction of the dry-weather baseline — the typical resting water level during dry periods. Rising means capacity is slowly being lost (sediment, grease, root intrusion). Falling means it's recovering. The rate is in millimetres per month; the confidence label tells you how statistically certain the trend is." />
									</div>
									<TrendDirectionBadge direction={trend.buildupTrend} />
									{trend.buildupRateMmMonth !== null && (
										<div className="text-xs text-muted-foreground">
											{trend.buildupRateMmMonth > 0 ? "+" : ""}
											{trend.buildupRateMmMonth.toFixed(2)} mm/mo
										</div>
									)}
								</div>

								{/* Months to limit */}
								<div className="space-y-1">
									<div className="flex items-center gap-1 text-xs text-muted-foreground">
										<Clock className="h-3 w-3" />
										Months to Threshold
										<InfoTooltip text="Projected months until the dry-weather baseline crosses the alarm threshold, at which point the pipe would be in permanent dry-weather alarm without intervention. Based on the current baseline drift rate; only populated when the baseline is rising. Separate from Est. Next Blockage, which forecasts the next acute event." />
									</div>
									{trend.buildupMonthsToLimit !== null ? (
										<>
											<div className={`text-lg font-semibold ${monthsToLimitColor}`}>
												{trend.buildupMonthsToLimit.toFixed(1)}
											</div>
											<div className="text-xs text-muted-foreground">at current rate</div>
										</>
									) : (
										<div className="text-lg font-semibold text-muted-foreground">—</div>
									)}
								</div>

								{/* Forecast confidence */}
								<div className="space-y-2">
									<div className="flex items-center gap-1 text-xs text-muted-foreground">
										Forecast Confidence{" "}
										<InfoTooltip text="How much to trust these trend estimates. Combines the statistical significance of the trend (p-value) with how much history is available. Below ~40% treat the numbers as directional rather than precise." />
									</div>
									{trend.forecastConfidence != null ? (
										<>
											<div className="text-lg font-semibold">
												{(trend.forecastConfidence * 100).toFixed(0)}%
											</div>
											<div className="h-1.5 w-full rounded bg-muted">
												<div
													className="h-1.5 rounded bg-blue-500"
													style={{
														width: `${Math.min(100, trend.forecastConfidence * 100)}%`,
													}}
												/>
											</div>
										</>
									) : (
										<div className="text-lg font-semibold text-muted-foreground">—</div>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
				{/* end Stats + Trend panel */}

				{/* Chart */}
				{loading && <Skeleton className="h-[300px] rounded-lg" />}

				{loaded && (
					<BlockageChart
						structureId={structureId}
						structureName={blockage.structureName}
						structureLat={blockage.coordinate.latitude}
						structureLon={blockage.coordinate.longitude}
						allParameters={allParameters}
						from={from}
						to={to}
						ranges={ranges}
						rainOverlay={rainOverlay}
						sourceInfo={sourceInfo}
						showSourceBadge={showSourceBadge}
					/>
				)}

				<Dialog
					open={selectedEvent !== null}
					onOpenChange={open => {
						if (!open) {
							setSelectedEvent(null);
							setDialogError(null);
						}
					}}
				>
					<DialogContent className="sm:max-w-md">
						<DialogHeader>
							<DialogTitle>{blockage.structureName}</DialogTitle>
							<DialogDescription>Was this a real blockage event?</DialogDescription>
						</DialogHeader>
						{selectedEvent &&
							(() => {
								const start = DateTime.fromMillis(selectedEvent.x, { zone: "utc" });
								const end = DateTime.fromMillis(selectedEvent.x2, { zone: "utc" });
								const totalSec = Math.round((selectedEvent.x2 - selectedEvent.x) / 1000);
								const h = Math.floor(totalSec / 3600);
								const m = Math.floor((totalSec % 3600) / 60);
								const duration = h > 0 ? `${h}h ${m}m` : `${m}m`;
								const feedback = feedbackMap.get(selectedEvent.x);

								return (
									<div className="space-y-5">
										<div className="flex items-center gap-3 rounded-lg bg-muted/50 px-4 py-3">
											<Clock className="h-4 w-4 text-muted-foreground shrink-0" />
											<div className="min-w-0">
												<p className="text-sm font-medium">
													{start.toFormat("d MMM yyyy")}
													{!start.hasSame(end, "day") && ` → ${end.toFormat("d MMM yyyy")}`}
												</p>
												<p className="text-xs text-muted-foreground">
													{start.toFormat("HH:mm")} → {end.toFormat("HH:mm")} · {duration}
												</p>
											</div>
										</div>

										{feedback ? (
											<div className="space-y-3">
												{/* Current classification status */}
												<div
													className={`rounded-lg border px-4 py-3 ${
														feedback.classification === "TruePositive"
															? "bg-green-50 border-green-200"
															: "bg-muted/40 border-border"
													}`}
												>
													<p
														className={`flex items-center gap-2 text-sm font-medium ${
															feedback.classification === "TruePositive"
																? "text-green-700"
																: "text-muted-foreground"
														}`}
													>
														{feedback.classification === "TruePositive" ? (
															<>
																<Check className="h-4 w-4" /> Confirmed real blockage
															</>
														) : (
															<>
																<X className="h-4 w-4" /> Marked as false positive
															</>
														)}
													</p>
													<p className="mt-0.5 text-xs text-muted-foreground">
														By{" "}
														{/^[0-9a-f-]{36}$/i.test(feedback.classifiedBy)
															? "a team member"
															: feedback.classifiedBy}{" "}
														· {DateTime.fromISO(feedback.classifiedAt).toRelative()}
													</p>
												</div>

												{dialogError && <p className="text-xs text-red-500">{dialogError}</p>}

												{/* Actions */}
												<div className="flex flex-col gap-1.5">
													{feedback.classification === "TruePositive" ? (
														<button
															disabled={dialogSubmitting}
															onClick={() => handleDialogClassify("FalsePositive")}
															className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
														>
															<X className="h-4 w-4" /> No, false positive
														</button>
													) : (
														<button
															disabled={dialogSubmitting}
															onClick={() => handleDialogClassify("TruePositive")}
															className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
														>
															<Check className="h-4 w-4" /> Yes, real blockage
														</button>
													)}
													<button
														disabled={dialogSubmitting}
														onClick={() => handleDialogUndo(selectedEvent.x)}
														className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
													>
														<RotateCcw className="h-3.5 w-3.5" /> Remove classification
													</button>
												</div>
											</div>
										) : (
											<div className="flex flex-col gap-2">
												{dialogError && <p className="text-xs text-red-500">{dialogError}</p>}
												<button
													disabled={dialogSubmitting}
													onClick={() => handleDialogClassify("TruePositive")}
													className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
												>
													<Check className="h-4 w-4" /> Yes, real blockage
												</button>
												<button
													disabled={dialogSubmitting}
													onClick={() => handleDialogClassify("FalsePositive")}
													className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
												>
													<X className="h-4 w-4" /> No, false positive
												</button>
											</div>
										)}
									</div>
								);
							})()}
					</DialogContent>
				</Dialog>
			</div>
		);
	},
);

export const ExpandedBlockagePanel: React.FC<ExpandedBlockagePanelProps> = ({ row }) => (
	<BlockageDetailsPanel blockage={row.original} />
);
